import { useCallback, useEffect, useState } from 'react'
import { Table, Button, Col, Spin, Switch, notification } from 'antd'

import { ColumnsType } from 'antd/lib/table/interface'
import { useApi } from 'admin/lib/Api'
import { colours } from '@qflow/theme'
import { useParams } from 'react-router-dom'

import { Project } from './OneClickLca'

type EditingState = {
  isLoadingProjects?: boolean
  isSaving?: boolean
  isOneClickLcaEnabled: boolean
}

export function EditOneClickLca({ projects }: { projects: any[] }) {
  const { accountId } = useParams<{
    accountId: string
  }>()

  const [editingQflowProject, setEditingQflowProject] = useState<Project>()

  const [editingState, setEditingState] = useState<EditingState>({
    isLoadingProjects: false,
    isOneClickLcaEnabled: false
  })

  const api = useApi()

  const displayError = useCallback((errMsg: string, error: any) => {
    console.error(errMsg, error)
    notification.error({
      message: `Error`,
      description: errMsg,
      placement: 'top',
      style: { width: '90%' }
    })
  }, [])

  const setOneClickLca = useCallback((isOneClickLcaEnabled: boolean) => {
    setEditingState({
      isOneClickLcaEnabled: isOneClickLcaEnabled
    })
  }, [])

  const setEditing = useCallback((qflowProject?: Project) => {
    setEditingState({
      isLoadingProjects: false,
      isOneClickLcaEnabled: qflowProject?.isOneClickLcaEnabled ?? false
    })
    setEditingQflowProject(qflowProject)
  }, [])

  const setSaving = useCallback(() => {
    setEditingState(prev => ({
      ...prev,
      isSaving: true
    }))
  }, [])

  useEffect(() => {
    if (!editingState.isSaving || !api || !editingQflowProject) return

    const selectedProject: Project = {
      ...editingQflowProject,
      isOneClickLcaEnabled: editingState.isOneClickLcaEnabled
    }

    async function SaveChanges() {
      if (!editingQflowProject) {
        setEditingState(prev => ({
          ...prev,
          isSaving: false
        }))
        return
      }

      const [, status, error] = await api!.updateProject(
        accountId ? accountId : '',
        editingQflowProject!.projectId,
        selectedProject
      )

      if (error) {
        displayError(
          `Failed to save One Click LCA changes to project '${editingQflowProject?.name}'. [${status}] ${error.toString()}`,
          error
        )
        setEditingState(prev => ({
          ...prev,
          isSaving: false
        }))
        return
      }

      // Apply successful changes to local instance
      editingQflowProject!.isOneClickLcaEnabled =
        editingState.isOneClickLcaEnabled ? true : false

      notification.success({
        message: `One Click LCA setting successfully saved.`,
        description: `One Click LCA setting was successfully saved for project '${editingQflowProject?.name}'`,
        placement: 'top'
      })
      setEditing()
    }

    void SaveChanges()
  }, [
    accountId,
    api,
    displayError,
    editingQflowProject,
    editingState.isSaving,
    editingState.isOneClickLcaEnabled,
    setEditing,
    projects
  ])

  const renderQflowProjectColumn = useCallback(
    (qflowProject: Project) => {
      if (
        editingQflowProject &&
        editingQflowProject.projectId !== qflowProject.projectId
      ) {
        return (
          <span style={{ fontWeight: 700, opacity: '0.3' }}>
            {qflowProject.name}
          </span>
        )
      }
      return <span style={{ fontWeight: 700 }}>{qflowProject.name}</span>
    },
    [editingQflowProject]
  )

  const renderOneClickLcaColumn = useCallback(
    (qflowProject: Project) => {
      if (editingQflowProject === qflowProject) {
        return (
          <Switch
            defaultChecked={editingQflowProject.isOneClickLcaEnabled}
            disabled={editingState.isSaving}
            onChange={value => {
              setOneClickLca(value)
            }}
          />
        )
      } else {
        return qflowProject.isOneClickLcaEnabled ? (
          <span style={{ color: colours.GREEN }}>
            <strong>Enabled</strong>
          </span>
        ) : (
          <span style={{ color: colours.RED }}>
            <strong>Disabled</strong>
          </span>
        )
      }
    },
    [editingQflowProject, editingState.isSaving, setOneClickLca]
  )

  const renderEditColumn = useCallback(
    (qflowProject: Project) => {
      return editingQflowProject !== qflowProject ? (
        <Button
          onClick={() => setEditing(qflowProject)}
          disabled={!!editingQflowProject}
        >
          Edit
        </Button>
      ) : (
        <Col>
          <Button
            style={{ marginRight: '10px' }}
            onClick={() => setEditing()}
            disabled={editingState.isSaving}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            disabled={editingState.isSaving}
            onClick={setSaving}
            aria-label="Save"
            style={{ width: '60px' }}
          >
            <SaveButtonText isSaving={editingState.isSaving} />
          </Button>
        </Col>
      )
    },
    [editingQflowProject, editingState.isSaving, setEditing, setSaving]
  )

  const columns: ColumnsType<Project> = [
    {
      title: 'Project Name',
      render: (_, qflowProject, __) => renderQflowProjectColumn(qflowProject),
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: 'One Click LCA Enabled',
      render: (_, qflowProject, __) => renderOneClickLcaColumn(qflowProject),
      width: '30%'
    },
    {
      title: '',
      key: 'edit',
      width: '11rem',
      render: (_, qflowProject, __) => renderEditColumn(qflowProject),
      align: 'right'
    }
  ]

  return (
    <Table
      columns={columns}
      dataSource={projects}
      rowKey={row => row.projectId}
      pagination={false}
      style={{ width: '100%', margin: '3rem 1rem' }}
      loading={editingState.isLoadingProjects}
    />
  )
}

function SaveButtonText({ isSaving }: { isSaving?: boolean }) {
  if (!isSaving) return <>Save</>

  return <Spin size="small" />
}
